import React from 'react';
import PropTypes from 'prop-types';
import './TextField.css';

export default class TextField extends React.Component {

    static propTypes = {
        value: PropTypes.string,
        id: PropTypes.string,
        inputId: PropTypes.string,
        className: PropTypes.string,
        width: PropTypes.number,
        fullWidth: PropTypes.bool,
        disabled: PropTypes.bool,
        multiLine: PropTypes.bool,
        maxRows: PropTypes.number,
        dense: PropTypes.bool,
        floatingLabel: PropTypes.bool,
        labelText: PropTypes.string,
        placeholderText: PropTypes.string,
        autoCompleteText: PropTypes.string,
        helperText: PropTypes.string,
        error: PropTypes.bool,
        errorText: PropTypes.string,
        onFocus: PropTypes.func,
        onBlur: PropTypes.func,
        onChange: PropTypes.func,
        inputProps: PropTypes.object
    };

    static defaultProps = {
        maxRows: 4
    };

    constructor(props) {
        super(props);

        this.onFocus = this.onFocus.bind(this);
        this.onBlur = this.onBlur.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onMouseOver = this.onMouseOver.bind(this);
        this.onMouseOut = this.onMouseOut.bind(this);

        this.state = {
            isFocused: false,
            isHovering: false,
            value: this.props.value || '',
            rows: 1
        };
    }

    componentWillReceiveProps(nextProps) {
        let newState = {
            value: nextProps.value || ''
        };
        // Reset isFocused and isHovering in case disabled was set while the input was hovered over or in focus.
        if (nextProps.disabled && (this.state.isFocused || this.state.isHovering)) {
            newState.isFocused = false;
            newState.isHovering = false;
        }
        this.setState(newState);
    }

    onFocus() {
        this.setState({isFocused: true});
        if (this.props.onFocus) {
            this.props.onFocus(event);
        }
    }

    onBlur() {
        this.setState({isFocused: false});
        if (this.props.onBlur) {
            this.props.onBlur(event);
        }
    }

    onChange(event) {
        let newState = {value: event.target.value};
        if (this.props.multiLine) {
            newState.rows = this.calculateMultiLineRows(event.target);
        }
        this.setState(newState);
        if (this.props.onChange) {
            this.props.onChange(event);
        }
    }

    onMouseOver() {
        if (!this.state.isHovering) {
            this.setState({isHovering: true});
        }
    }

    onMouseOut(event) {
        let related = event.relatedTarget;
        let current = event.currentTarget;
        while (related && related !== current) {
            related = related.parentNode;
        }
        if (related === null) {
            this.setState({isHovering: false});
        }
    }

    calculateMultiLineRows(element) {
        let origHeight = element.style.height;
        element.style.height = 0;
        let lineHeight = parseInt(window.getComputedStyle(element).getPropertyValue('line-height'), 10);
        let scrollHeight = element.scrollHeight;
        let rows = scrollHeight / lineHeight;
        element.style.height = origHeight;
        return Math.min(rows, this.props.maxRows);
    }

    isValueEmpty() {
        return this.state.value === '';
    }

    rootClassName() {
        let rootClassNames = ["materialTextField"];
        if (this.state.isFocused) {
            rootClassNames.push("materialFocus");
        }
        if (this.state.isHovering) {
            rootClassNames.push("materialHover");
        }
        if (this.isValueEmpty()) {
            rootClassNames.push("materialEmpty");
        }
        if (!this.props.floatingLabel && this.showLabel()) {
            rootClassNames.push("materialResting");
        }
        if (this.props.error) {
            rootClassNames.push("materialError");
        }
        if (this.props.dense) {
            rootClassNames.push("dense");
        }
        if (this.props.fullWidth) {
            rootClassNames.push("fullWidth");
        }
        if (this.props.disabled) {
            rootClassNames.push("materialDisabled");
        }
        if (this.props.className) {
            Array.prototype.push.apply(rootClassNames, this.props.className.split(' '));
        }
        return rootClassNames.join(' ');
    }

    rootProps() {
        let props = {};
        if (this.props.id) {
            props.id = this.props.id;
        }
        if (this.props.width) {
            props.style = Object.assign({}, props.style, {width: this.props.width});
        }
        return props;
    }

    showLabel() {
        return (this.props.labelText || this.props.labelText === '') && !this.props.fullWidth;
    }

    label() {
        if (!this.showLabel()) {
            return null;
        }
        let props = {};
        if (this.props.inputId) {
            props.htmlFor = this.props.inputId;
        }
        return (
            <div className="materialLabelContainer">
                <label className="materialLabel" {...props}>{this.props.labelText}</label>
            </div>
        );
    }

    placeholder() {
        if (!this.props.placeholderText || !this.isValueEmpty()) {
            return null;
        }
        return (
            <div className="materialPlaceholder">{this.props.placeholderText}</div>
        );
    }

    autocomplete() {
        if (!this.props.autoCompleteText || !this.state.isFocused) {
            return null;
        }
        return (
            <div className="materialAutoComplete">
                <span className="materialAutoCompletePrefix">{this.state.value}</span>
                <span>{this.props.autoCompleteText}</span>
            </div>
        );
    }

    input() {
        let props = Object.assign({}, this.props.inputProps, {
            value: this.state.value
        });
        if (!this.props.disabled) {
            props.onChange = this.onChange;
            props.onFocus = this.onFocus;
            props.onBlur = this.onBlur;
            props.onMouseOver = this.onMouseOver;
            props.onMouseOut = this.onMouseOut;
        } else {
            props.disabled = this.props.disabled;
        }
        if (this.props.inputId) {
            props.id = this.props.inputId;
        }
        if (this.props.multiLine) {
            props.rows = this.state.rows;
            return <textarea className="materialInput" {...props}/>;
        } else {
            return <input className="materialInput" {...props}/>;
        }
    }

    inputLine() {
        if (this.props.fullWidth) {
            return null;
        }
        return (
            <div className="materialInputLineContainer">
                <div className="materialInputLine"/>
                <div className="materialInputLineHover"/>
                <div className="materialInputLineFocus"/>
                <div className="materialInputLineError"/>
            </div>
        );
    }

    helperText() {
        if (this.props.fullWidth) {
            return null;
        }
        let helperText = (this.props.error && this.props.errorText) || this.props.helperText;
        if (!helperText) {
            return null;
        }
        return (
            <div className="materialHelpText">{helperText}</div>
        );
    }

    render() {
        return (
            <div className={this.rootClassName()} {...this.rootProps()}>
                {this.label()}
                <div className="materialInputContainer">
                    {this.placeholder()}
                    {this.autocomplete()}
                    {this.input()}
                </div>
                {this.inputLine()}
                {this.helperText()}
            </div>
        );
    }

}



// WEBPACK FOOTER //
// ./src/components/material/TextField.js